import '../styles/pdf-pages.css';
import '../styles/model-overview.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateTourEss } from '@/domain/impact-calculate-tour/entities/calculate-tour-ess.entity';
import { selectImpactCalculateTourEssForProject } from '@/domain/impact-calculate-tour/impact-calculate-tour.selectors';
import { ProjectTour } from '@/domain/project-tour/entities/project-tour.entity';
import { selectCurrentProjectTour } from '@/domain/project-tour/project-tour.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatProjectDatesTz } from '@/ui/utils/format-project-dates';

import CalendarMonthSvg from '../assets/calendar_month.svg';
import { LittleGauge } from '../components/LittleGauge';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import { displayDecimal } from '../utils/display-scores.utils';

export function PdfTourPage5({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const tour = useAppSelector(
    selectCurrentProjectTour,
  ) satisfies ProjectTour | null;
  const tourId = tour?.id ?? 0;

  const impactEss = useAppSelector(
    selectImpactCalculateTourEssForProject(tourId),
  ) satisfies CalculateTourEss;

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!tour) {
    return <h1>{translate('PdfExports.tour-not-found')}</h1>;
  }

  const general = impactEss.general;
  const topics = general.topics;
  const impactEssScore100 =
    Math.round((general.score * 100) / general.maxScore) || 0;

  return (
    <div id="page5" className="pdf-page">
      <div className="content">
        <div className="pdf-light-header">
          <div>
            <h1>{tour.name}</h1>
            {tour.startDate && tour.endDate && (
              <h2>
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="tour-date">
                  {formatProjectDatesTz(tour.startDate, tour.endDate)}
                </span>
              </h2>
            )}
          </div>
        </div>
        <div className="pdf-separator"></div>

        <div className="model-header">
          <div>
            <h2>{translate('PdfExports.ess-score-details')}</h2>
          </div>
          <div>
            <h4>
              <span className="highlight">{impactEssScore100}</span>/ 100
            </h4>
            <h5>
              {general.score} / {general.maxScore}
            </h5>
          </div>
          <div>
            <PdfScoreCard
              type="ess"
              scoreLetter={general.scoreLetter}
              size="medium"
            />
          </div>
        </div>

        <div className="model-content">
          <div
            style={{
              width: 360,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="sector">
              <div className="sector-header">
                <h4 style={{ width: 240 }}>
                  {translate('Ess.Topics.PARITY_EQUALITY')}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(topics.PARITY_EQUALITY.score)}
                  </span>{' '}
                  / {topics.PARITY_EQUALITY.maxScore}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(topics.PARITY_EQUALITY.sections) as [
                      keyof typeof topics.PARITY_EQUALITY.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Ess.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {maxScore}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={topics.PARITY_EQUALITY.score}
                  maxScore={topics.PARITY_EQUALITY.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4 style={{ width: 240 }}>
                  {translate('Ess.Topics.ACCESSIBILITY_SOCIAL_DIALOGUE')}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(topics.ACCESSIBILITY_SOCIAL_DIALOGUE.score)}
                  </span>{' '}
                  / {topics.ACCESSIBILITY_SOCIAL_DIALOGUE.maxScore}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      topics.ACCESSIBILITY_SOCIAL_DIALOGUE.sections,
                    ) as [
                      keyof typeof topics.ACCESSIBILITY_SOCIAL_DIALOGUE.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Ess.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {maxScore}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={topics.ACCESSIBILITY_SOCIAL_DIALOGUE.score}
                  maxScore={topics.ACCESSIBILITY_SOCIAL_DIALOGUE.maxScore}
                />
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4 style={{ width: 240 }}>
                  {translate('Ess.Topics.LOCAL_IMPACT_RSE_ENGAGEMENT')}
                </h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(topics.LOCAL_IMPACT_RSE_ENGAGEMENT.score)}
                  </span>{' '}
                  / {topics.LOCAL_IMPACT_RSE_ENGAGEMENT.maxScore}
                </div>
              </div>
              <div className="sector-value" style={{ display: 'flex' }}>
                <ul style={{ width: '200px' }}>
                  {(
                    Object.entries(
                      topics.LOCAL_IMPACT_RSE_ENGAGEMENT.sections,
                    ) as [
                      keyof typeof topics.LOCAL_IMPACT_RSE_ENGAGEMENT.sections,
                      { score: number; maxScore: number },
                    ][]
                  ).map(([key, { score, maxScore }]) => (
                    <li key={key}>
                      <span>{translate(`Ess.Sections.${key}`)}</span>
                      <span>
                        {displayDecimal(score)} / {maxScore}
                      </span>
                    </li>
                  ))}
                </ul>
                <LittleGauge
                  score={topics.LOCAL_IMPACT_RSE_ENGAGEMENT.score}
                  maxScore={topics.LOCAL_IMPACT_RSE_ENGAGEMENT.maxScore}
                />
              </div>
            </div>
          </div>
          <div
            style={{
              width: 340,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="model-overall-comments">
              {translate('PdfExports.comment')}
            </div>
          </div>
        </div>

        <div className="navigation-controls">
          <a href="#page4" className="tag-pill">
            {translate('PdfExports.go-back-to-env')}
          </a>
        </div>
      </div>

      <PdfProjectPager current={5} total={totalPages}></PdfProjectPager>
    </div>
  );
}
